// TEMPORÁRIO — mock local, remover no desenvolvimento do backend.
import type {
  DreOpcao,
  DreResumoApuracao,
  MovimentoApuracaoLinha,
  FechamentoLinha,
  MedicaoCicloLinha,
  LoteSofLinha,
  EmpenhoLinha,
  MedicaoDetalhe,
  MedicaoItemLinha,
  AjusteMedicao,
  ObservacaoEstruturada,
  SofTimelineMedicao,
  RevisaoMedicao,
  PedidoPortalMedicao,
  DocumentoMedicao,
  ChecklistDocumentoObrigatorio,
  CapacidadeBancadaLinha,
  AlteracaoHistoricoMedicao,
  ApontamentoDiaLinha,
  CelulaApontamentoDia,
  ModalidadeAtendimento,
  StatusApontamentoOs,
  ComposicaoValoresLinha,
  LoteAtesteImportado,
  ReservaSofLinha,
  StatusSofLinha,
  ConciliacaoSofItem,
  RelatorioFiscalConsolidadoDre,
  RelatorioFiscalCredenciado,
  ExportacaoFiscalHistoricoLinha,
  AcompanhamentoMensalLinha,
  LancamentoOsFormulario,
  DemonstrativoLinha,
  LoteSmeMestre,
  FechamentoV2Resumo,
  FechamentoV2ListaItem,
} from './types';

/**
 * Competência a `deslocamento` meses do mês corrente (0 = este mês, -1 = o
 * anterior), em `AAAA-MM-01`. O mock do Fechamento Mensal V2 não guarda mês
 * literal: a tela abre sempre no mês anterior ao de hoje, e data fixa esvazia
 * essa entrada sozinha assim que o calendário vira.
 */
function competenciaRelativa(deslocamento: number): string {
  const hoje = new Date();
  const d = new Date(hoje.getFullYear(), hoje.getMonth() + deslocamento, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

/** `AAAA-MM` de uma competência `AAAA-MM-01`, para compor número de lote e de empenho. */
function anoMes(competencia: string): string {
  return competencia.slice(0, 7);
}

/**
 * Competências dos mocks de ciclo, lote e empenho: há dois e há três meses, a
 * mesma distância que estes dados guardavam quando a data era escrita à mão.
 * Sem ano-mês literal — `dadosSimulados.spec.ts` falha se alguém escrever um.
 */
const COMPETENCIA_RECENTE = competenciaRelativa(-2);
const COMPETENCIA_ANTERIOR = competenciaRelativa(-3);
const ANO_MES_RECENTE = anoMes(COMPETENCIA_RECENTE);
const ANO_MES_ANTERIOR = anoMes(COMPETENCIA_ANTERIOR);

/** As 13 Diretorias Regionais de Educação da SME/SP. */
export const MOCK_DRES: DreOpcao[] = [
  { id: 'dre-1', codigo: 'BT', nome: 'Butantã' },
  { id: 'dre-2', codigo: 'CL', nome: 'Campo Limpo' },
  { id: 'dre-3', codigo: 'G', nome: 'Guaianases' },
  { id: 'dre-4', codigo: 'IP', nome: 'Ipiranga' },
  { id: 'dre-5', codigo: 'CS', nome: 'Capela do Socorro' },
  { id: 'dre-6', codigo: 'FB', nome: 'Freguesia/Brasilândia' },
  { id: 'dre-7', codigo: 'IQ', nome: 'Itaquera' },
  { id: 'dre-8', codigo: 'JT', nome: 'Jaçanã/Tremembé' },
  { id: 'dre-9', codigo: 'MP', nome: 'Penha' },
  { id: 'dre-10', codigo: 'PJ', nome: 'Pirituba/Jaraguá' },
  { id: 'dre-11', codigo: 'SA', nome: 'Santo Amaro' },
  { id: 'dre-12', codigo: 'SM', nome: 'São Miguel' },
  { id: 'dre-13', codigo: 'MT', nome: 'São Mateus' },
];

export const MOCK_APURACAO_RESUMO: DreResumoApuracao[] = [
  {
    dreId: 'dre-1',
    dreCodigo: 'BT',
    dreNome: 'Butantã',
    osAtivas: 120,
    movimentosLancados: 100,
    pendentes: 20,
    totalFaltas: 5,
  },
  {
    dreId: 'dre-2',
    dreCodigo: 'CL',
    dreNome: 'Campo Limpo',
    osAtivas: 85,
    movimentosLancados: 85,
    pendentes: 0,
    totalFaltas: 12,
  },
  {
    dreId: 'dre-3',
    dreCodigo: 'G',
    dreNome: 'Guaianases',
    osAtivas: 200,
    movimentosLancados: 0,
    pendentes: 200,
    totalFaltas: 0,
  },
  {
    dreId: 'dre-4',
    dreCodigo: 'IP',
    dreNome: 'Ipiranga',
    osAtivas: 50,
    movimentosLancados: 50,
    pendentes: 0,
    totalFaltas: 2,
  },
];

export const MOCK_APURACAO_MOVIMENTOS: MovimentoApuracaoLinha[] = [
  {
    ordemServicoId: 'os-1',
    numeroOs: 1001,
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    modalidade: 'CONVENCIONAL',
    kmRotaBase: '45.50',
    diasUteisMes: 22,
    diasOperados: 22,
    diasFalta: 0,
    kmRodados: '1001.00',
    diasAtivComplementarPresencial: 0,
    diasAtivComplementarRemoto: 0,
    ajusteValor: '0.00',
    status: 'RASCUNHO',
  },
  {
    ordemServicoId: 'os-2',
    numeroOs: 1002,
    credenciadoNome: 'Viação Escolar Zona Leste',
    modalidade: 'CRECHE',
    kmRotaBase: '30.00',
    diasUteisMes: 22,
    diasOperados: 20,
    diasFalta: 2,
    kmRodados: '600.00',
    diasAtivComplementarPresencial: 1,
    diasAtivComplementarRemoto: 0,
    ajusteValor: '-150.00',
    status: 'RASCUNHO',
  },
  {
    ordemServicoId: 'os-3',
    numeroOs: 1003,
    credenciadoNome: 'Cooperativa de Vans SP',
    modalidade: 'ACESSIVEL',
    kmRotaBase: '12.40',
    diasUteisMes: 22,
    diasOperados: 22,
    diasFalta: 0,
    kmRodados: '272.80',
    diasAtivComplementarPresencial: 0,
    diasAtivComplementarRemoto: 0,
    ajusteValor: '500.00',
    status: 'RASCUNHO',
  },
];

export const MOCK_FECHAMENTOS: FechamentoLinha[] = [
  {
    id: 'f-1',
    mesReferencia: COMPETENCIA_RECENTE,
    dreId: 'dre-1',
    dreCodigo: 'BT',
    dreNome: 'Butantã',
    status: 'EM_FECHAMENTO',
    totalMedicoes: 120,
    totalCredenciados: 45,
    valorBruto: '1500000.00',
    valorDescontos: '5000.00',
    valorLiquido: '1495000.00',
    fechadoEm: null,
  },
  {
    id: 'f-2',
    mesReferencia: COMPETENCIA_ANTERIOR,
    dreId: 'dre-2',
    dreCodigo: 'CL',
    dreNome: 'Campo Limpo',
    status: 'FECHADO',
    totalMedicoes: 85,
    totalCredenciados: 30,
    valorBruto: '900000.00',
    valorDescontos: '0.00',
    valorLiquido: '900000.00',
    fechadoEm: '2026-08-05T10:00:00Z',
  },
  {
    id: 'f-3',
    mesReferencia: COMPETENCIA_RECENTE,
    dreId: 'dre-3',
    dreCodigo: 'G',
    dreNome: 'Guaianases',
    status: 'ABERTO',
    totalMedicoes: 200,
    totalCredenciados: 80,
    valorBruto: '0.00',
    valorDescontos: '0.00',
    valorLiquido: '0.00',
    fechadoEm: null,
  },
];

export const MOCK_CICLO: MedicaoCicloLinha[] = [
  {
    id: 'm-1',
    credenciadoId: 'c-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    natureza: 'PJ',
    mesReferencia: COMPETENCIA_RECENTE,
    status: 'EMPENHADA',
    valorTotal: '25000.00',
  },
  {
    id: 'm-2',
    credenciadoId: 'c-2',
    credenciadoNome: 'Viação Escolar Zona Leste',
    natureza: 'PJ',
    mesReferencia: COMPETENCIA_RECENTE,
    status: 'LIQUIDADA',
    valorTotal: '18500.00',
  },
  {
    id: 'm-3',
    credenciadoId: 'c-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    natureza: 'PJ',
    mesReferencia: COMPETENCIA_RECENTE,
    status: 'APROVADA',
    valorTotal: '42300.00',
  },
  {
    id: 'm-4',
    credenciadoId: 'c-4',
    credenciadoNome: 'João Motorista ME',
    natureza: 'PF',
    mesReferencia: COMPETENCIA_ANTERIOR,
    status: 'PAGA',
    valorTotal: '12400.00',
  },
];

export const MOCK_SOF: LoteSofLinha[] = [
  {
    id: 'l-1',
    numeroLote: `LOTE-${ANO_MES_RECENTE}-001`,
    mesReferencia: COMPETENCIA_RECENTE,
    dreId: 'dre-1',
    dreNome: 'Butantã',
    status: 'CONFIRMADO_SOF',
    totalCredenciados: 45,
    totalEmpenhos: 120,
    valorTotal: '1495000.00',
    enviadoSofEm: '2026-08-10T14:30:00Z',
  },
  {
    id: 'l-2',
    numeroLote: `LOTE-${ANO_MES_RECENTE}-002`,
    mesReferencia: COMPETENCIA_RECENTE,
    dreId: 'dre-2',
    dreNome: 'Campo Limpo',
    status: 'ENVIADO_SOF',
    totalCredenciados: 30,
    totalEmpenhos: 85,
    valorTotal: '900000.00',
    enviadoSofEm: '2026-08-20T09:15:00Z',
  },
  {
    id: 'l-3',
    numeroLote: `LOTE-${ANO_MES_ANTERIOR}-010`,
    mesReferencia: COMPETENCIA_ANTERIOR,
    dreId: null,
    dreNome: null,
    status: 'CONSOLIDADO',
    totalCredenciados: 120,
    totalEmpenhos: 350,
    valorTotal: '4250000.00',
    enviadoSofEm: null,
  },
];

export const MOCK_EMPENHOS: EmpenhoLinha[] = [
  {
    id: 'ne-1',
    numeroNe: `NE-${ANO_MES_RECENTE}-0451`,
    dotacaoOrcamentaria: '16.10.12.365.3013.2100.339039',
    loteId: 'l-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    mesReferencia: COMPETENCIA_RECENTE,
    valor: '22800.00',
    status: 'EMPENHADO',
  },
  {
    id: 'ne-2',
    numeroNe: `NE-${ANO_MES_RECENTE}-0452`,
    dotacaoOrcamentaria: '16.10.12.365.3013.2100.339039',
    loteId: 'l-1',
    credenciadoNome: 'Cooperativa de Vans SP',
    mesReferencia: COMPETENCIA_RECENTE,
    valor: '38070.00',
    status: 'EMPENHADO',
  },
  {
    id: 'ne-3',
    numeroNe: `NE-${ANO_MES_RECENTE}-0480`,
    dotacaoOrcamentaria: '16.10.12.365.3013.2100.339039',
    loteId: 'l-2',
    credenciadoNome: 'Van Escolar Beta Ltda',
    mesReferencia: COMPETENCIA_RECENTE,
    valor: '19614.00',
    status: 'EMPENHADO',
  },
  {
    id: 'ne-4',
    numeroNe: `NE-${ANO_MES_ANTERIOR}-0398`,
    dotacaoOrcamentaria: '16.10.12.365.3013.2100.339039',
    loteId: 'l-3',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    mesReferencia: COMPETENCIA_ANTERIOR,
    valor: '21500.00',
    status: 'PRE_EMPENHO',
  },
  {
    id: 'ne-5',
    numeroNe: `NE-${ANO_MES_ANTERIOR}-0399`,
    dotacaoOrcamentaria: '16.10.12.365.3013.2100.339039',
    loteId: 'l-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    mesReferencia: COMPETENCIA_ANTERIOR,
    valor: '35200.00',
    status: 'ANULADO',
  },
];

/* =====================================================================
 * Medição — detalhe (FIN-002)
 * ===================================================================== */

export const MOCK_MEDICOES: MedicaoDetalhe[] = [
  {
    id: 'm-1',
    mesReferencia: COMPETENCIA_RECENTE,
    credenciadoId: 'c-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    status: 'APROVADA',
    termoSei: 'SEI-6017.2026/0001234-5',
    valorBruto: '25000.00',
    valorLiquido: '22800.00',
    retencoesPf: '1800.00',
    descontosMultas: '400.00',
    descontosCobrancas: '0.00',
    processadoEm: '2026-08-05T10:00:00Z',
    aprovadoEm: '2026-08-07T14:30:00Z',
    calculoDesatualizado: false,
    alertaCompetenciaIrInssMeses: null,
    previsaoCredito: '2026-08-20',
    obsFin: null,
    obsDre: 'Confirmado apontamento sem pendência.',
    loteInstitucionalStatus: 'APROVADO',
    numeroRevisoes: 1,
    numeroDocumentos: 3,
    riscoFiscal: 'REGULAR',
    divergenciasPotenciais: 0,
  },
  {
    id: 'm-3',
    mesReferencia: COMPETENCIA_RECENTE,
    credenciadoId: 'c-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    status: 'PROCESSADA',
    termoSei: 'SEI-6017.2026/0004521-1',
    valorBruto: '42300.00',
    valorLiquido: '38070.00',
    retencoesPf: '0.00',
    descontosMultas: '0.00',
    descontosCobrancas: '4230.00',
    processadoEm: '2026-08-06T09:15:00Z',
    aprovadoEm: null,
    calculoDesatualizado: true,
    alertaCompetenciaIrInssMeses: 2,
    previsaoCredito: null,
    obsFin: 'Aguardando revisão da tabela de retenção de cooperativa.',
    obsDre: null,
    loteInstitucionalStatus: 'EM_CONSOLIDACAO',
    numeroRevisoes: 0,
    numeroDocumentos: 1,
    riscoFiscal: 'CRITICO',
    divergenciasPotenciais: 2,
  },
];

export const MOCK_MEDICAO_ITENS: Record<string, MedicaoItemLinha[]> = {
  'm-1': [
    {
      ordemServicoId: 'os-1',
      numeroOs: 1001,
      tipoVeiculo: 'Van',
      km: '1001.00',
      dias: 22,
      valorKm: '2.10',
      totalKm: '2102.10',
      valorDia: '1000.00',
      totalDias: '22000.00',
      ativContinuada: true,
      retencoesPf: '1800.00',
      descMultas: '400.00',
      descCobrancas: '0.00',
      totalBruto: '25000.00',
    },
  ],
};

export const MOCK_MEDICAO_AJUSTES: Record<string, AjusteMedicao[]> = {
  'm-1': [
    {
      id: 'aj-1',
      quando: '2026-08-06T11:00:00Z',
      tipo: 'Correção de KM',
      ordemServicoId: 'os-1',
      valor: '-150.00',
      status: 'ATIVO',
      motivo: 'Rota reduzida por obra',
      justificativa: 'Confirmado pela DRE em ofício interno.',
    },
  ],
};

export const MOCK_MEDICAO_OBSERVACOES: Record<string, ObservacaoEstruturada[]> = {
  'm-1': [
    {
      id: 'obs-1',
      origem: 'DRE',
      motivo: 'Confirmação de apontamento',
      destinatario: 'Financeiro',
      texto: 'Apontamento confirmado sem divergência.',
      criadoEm: '2026-08-05T12:00:00Z',
    },
  ],
};

export const MOCK_MEDICAO_SOF_TIMELINE: Record<string, SofTimelineMedicao> = {
  'm-1': {
    estagio: 'CONFIRMADO_SOF',
    rejeicaoMotivo: null,
    marcos: [
      { nome: 'Aprovada', dataHora: '2026-08-07T14:30:00Z' },
      { nome: 'Empenhada', dataHora: '2026-08-08T09:00:00Z' },
      { nome: 'Arquivo SOF gerado', dataHora: '2026-08-09T10:00:00Z' },
      { nome: 'Enviada ao SOF', dataHora: '2026-08-10T14:30:00Z' },
      { nome: 'Confirmada pelo SOF', dataHora: '2026-08-11T08:00:00Z' },
      { nome: 'Paga', dataHora: null },
    ],
    empenho: {
      numero: `NE-${ANO_MES_RECENTE}-0142`,
      data: '2026-08-08T09:00:00Z',
      valor: '22800.00',
      dotacao: '16.10.12.365.3013.2100',
      status: 'CONFIRMADO',
    },
    loteEmpenho: {
      numeroLote: `LOTE-${ANO_MES_RECENTE}-001`,
      protocoloSof: 'SOF-2026-081100421',
      status: 'CONFIRMADO_SOF',
    },
    arquivos: [
      {
        nome: `lote-${ANO_MES_RECENTE}-001.rem`,
        formato: 'REM',
        dataGeracao: '2026-08-09T10:00:00Z',
        valorExportado: '22800.00',
      },
    ],
  },
};

export const MOCK_MEDICAO_REVISOES: Record<string, RevisaoMedicao[]> = {
  'm-1': [
    {
      id: 'rev-1',
      tipo: 'CORRECAO_VALORES',
      motivo: 'Ajuste de KM rodado',
      valorOriginal: '25150.00',
      valorRevisado: '25000.00',
      parecer: 'Aprovado pelo DTP técnico.',
      status: 'APROVADA',
    },
  ],
};

export const MOCK_MEDICAO_PEDIDOS_PORTAL: Record<string, PedidoPortalMedicao[]> = {
  'm-3': [
    {
      id: 'ped-1',
      origem: 'Portal do credenciado',
      motivo: 'Divergência de valor apurado',
      resposta: null,
      status: 'PENDENTE',
    },
  ],
};

export const MOCK_MEDICAO_DOCUMENTOS: Record<string, DocumentoMedicao[]> = {
  'm-1': [
    {
      id: 'doc-1',
      tipo: 'NOTA_FISCAL',
      arquivoNome: 'nf-1001.pdf',
      enviadoEm: '2026-08-06T10:00:00Z',
    },
    {
      id: 'doc-2',
      tipo: 'COMPROVANTE_PAGAMENTO',
      arquivoNome: 'comprovante-08.pdf',
      enviadoEm: '2026-08-21T09:00:00Z',
    },
    {
      id: 'doc-3',
      tipo: 'RECIBO',
      arquivoNome: 'recibo-08.pdf',
      enviadoEm: '2026-08-21T09:05:00Z',
    },
  ],
};

export const MOCK_MEDICAO_CHECKLIST: Record<string, ChecklistDocumentoObrigatorio[]> = {
  'm-1': [
    {
      id: 'chk-1',
      nomeDocumento: 'Certidão Negativa de Débitos',
      status: 'APROVADO',
      validade: '2027-01-15',
      observacao: null,
    },
    {
      id: 'chk-2',
      nomeDocumento: 'Comprovante de Regularidade FGTS',
      status: 'PENDENTE',
      validade: null,
      observacao: null,
    },
  ],
};

export const MOCK_MEDICAO_CAPACIDADE: Record<string, CapacidadeBancadaLinha[]> = {
  'm-1': [
    {
      ordemServicoId: 'os-1',
      placa: 'ABC1D23',
      tipo: 'Van',
      inicioVigencia: competenciaRelativa(-9),
      fimVigencia: null,
      lugaresTotais: 15,
      descontados: 2,
      efetivos: 13,
      statusVistoria: 'APROVADA',
    },
  ],
};

export const MOCK_MEDICAO_HISTORICO: Record<string, AlteracaoHistoricoMedicao[]> = {
  'm-1': [
    {
      id: 'hist-1',
      campo: 'valorLiquido',
      valorAnterior: '22950.00',
      valorNovo: '22800.00',
      autorNome: 'Ana Ferreira',
      autorPapel: 'DTP',
      dataHora: '2026-08-07T14:00:00Z',
      motivo: 'Correção de KM aplicada',
    },
  ],
};

/* =====================================================================
 * Fechamento Mensal V2 — abas de detalhe (FIN-023.x)
 * ===================================================================== */

/** Dias úteis (seg–sex) de um mês — mesmo critério "letivo" simplificado do
 * protótipo (sem excluir feriados específicos, que dependem do calendário da
 * SME e ainda não têm fonte de dados). */
function diasUteisDoMes(ano: number, mes: number): number[] {
  const total = new Date(ano, mes, 0).getDate();
  const dias: number[] = [];
  for (let d = 1; d <= total; d++) {
    const diaSemana = new Date(ano, mes - 1, d).getDay();
    if (diaSemana !== 0 && diaSemana !== 6) dias.push(d);
  }
  return dias;
}

/** Gera as células diárias de uma OS a partir de um perfil de atendimento —
 * evita autorar 30+ campos por dia à mão para cada uma das OS do mock. */
function gerarCelulas(
  diasLetivos: number[],
  perfil: { naoCadeirante?: number; cadeirante?: number },
  opcoes: { diasVazios?: number[]; diaFalta?: number; diaFaltaVeiculo?: number } = {},
): Record<number, CelulaApontamentoDia> {
  const { diasVazios = [], diaFalta, diaFaltaVeiculo } = opcoes;
  const celulas: Record<number, CelulaApontamentoDia> = {};
  for (const d of diasLetivos) {
    const vazio = diasVazios.includes(d);
    celulas[d] = {
      naoCadeirante: vazio ? 0 : (perfil.naoCadeirante ?? 0),
      cadeirante: vazio ? 0 : (perfil.cadeirante ?? 0),
      continuadaNaoCadeirante: 0,
      continuadaCadeirante: 0,
      complementarNaoCadeirante: 0,
      complementarCadeirante: 0,
      faltasAlunos: d === diaFalta ? 1 : 0,
      faltaVeiculo: d === diaFaltaVeiculo,
    };
  }
  return celulas;
}

interface PerfilOsApontamento {
  ordemServicoId: string;
  numeroOs: number;
  credenciadoId: string;
  credenciadoNome: string;
  condutorNome?: string;
  condutorCrmc?: string;
  placaVeiculo?: string;
  tipoVeiculo?: string;
  monitorNome?: string;
  dreId: string;
  dreCodigo: string;
  dreNome: string;
  natureza: 'PF' | 'PJ';
  modalidade: ModalidadeAtendimento;
  status: StatusApontamentoOs;
  km: string;
  naoCadeirante?: number;
  cadeirante?: number;
  diasVazios?: number[];
  /** Nenhum dia lançado ainda (rascunho recém-aberto). */
  todosVazios?: boolean;
  diaFalta?: number;
  diaFaltaVeiculo?: number;
  /** Dia do mês em que a vigência da OS termina — os dias seguintes ficam
   * "fora da vigência" na grade (ex.: contrato encerrado no meio do mês). */
  vigenciaFimDia?: number;
}

const PERFIS_OS_APONTAMENTO: PerfilOsApontamento[] = [
  {
    ordemServicoId: 'os-1',
    numeroOs: 1001,
    credenciadoId: 'c-4',
    credenciadoNome: 'João Motorista ME',
    condutorNome: 'João Motorista ME',
    condutorCrmc: 'CRMC-4521',
    placaVeiculo: 'ABC1D23',
    tipoVeiculo: 'Van',
    monitorNome: 'Regina Alves',
    dreId: 'dre-1',
    dreCodigo: 'BT',
    dreNome: 'Butantã',
    natureza: 'PF',
    modalidade: 'CONVENCIONAL',
    status: 'APROVADO_SME',
    km: '45.50',
    naoCadeirante: 18,
    cadeirante: 2,
    diaFalta: 5,
  },
  {
    ordemServicoId: 'os-5',
    numeroOs: 1005,
    credenciadoId: 'c-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    condutorNome: 'Progresso Condutor',
    condutorCrmc: 'CRMC-5296',
    placaVeiculo: 'LOV0A10',
    tipoVeiculo: 'Ônibus',
    monitorNome: 'Monitor Souza',
    dreId: 'dre-1',
    dreCodigo: 'BT',
    dreNome: 'Butantã',
    natureza: 'PJ',
    modalidade: 'ACESSIVEL',
    status: 'DEVOLVIDO',
    km: '52.00',
    naoCadeirante: 10,
    cadeirante: 8,
    diasVazios: [4, 5],
  },
  {
    ordemServicoId: 'os-2',
    numeroOs: 1002,
    credenciadoId: 'c-2',
    credenciadoNome: 'Viação Escolar Zona Leste',
    dreId: 'dre-2',
    dreCodigo: 'CL',
    dreNome: 'Campo Limpo',
    natureza: 'PF',
    modalidade: 'CONVENCIONAL',
    status: 'RECEBIDO_DTP',
    km: '30.00',
    naoCadeirante: 25,
    vigenciaFimDia: 17,
  },
  {
    ordemServicoId: 'os-6',
    numeroOs: 1006,
    credenciadoId: 'c-5',
    credenciadoNome: 'Maria Condutora Autônoma',
    dreId: 'dre-2',
    dreCodigo: 'CL',
    dreNome: 'Campo Limpo',
    natureza: 'PF',
    modalidade: 'CRECHE',
    status: 'RASCUNHO_DRE',
    km: '18.00',
    naoCadeirante: 12,
    todosVazios: true,
  },
  {
    ordemServicoId: 'os-3',
    numeroOs: 1003,
    credenciadoId: 'c-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    dreId: 'dre-3',
    dreCodigo: 'G',
    dreNome: 'Guaianases',
    natureza: 'PJ',
    modalidade: 'CONVENCIONAL',
    status: 'RECEBIDO_DTP',
    km: '12.40',
    naoCadeirante: 30,
  },
  {
    ordemServicoId: 'os-7',
    numeroOs: 1007,
    credenciadoId: 'c-6',
    credenciadoNome: 'Vans Unidas Cooperativa',
    dreId: 'dre-3',
    dreCodigo: 'G',
    dreNome: 'Guaianases',
    natureza: 'PJ',
    modalidade: 'ACESSIVEL',
    status: 'APROVADO_SME',
    km: '21.30',
    naoCadeirante: 14,
    cadeirante: 6,
    diaFalta: 12,
  },
  {
    ordemServicoId: 'os-4',
    numeroOs: 1004,
    credenciadoId: 'c-7',
    credenciadoNome: 'Carlos Motorista Individual',
    dreId: 'dre-4',
    dreCodigo: 'IP',
    dreNome: 'Ipiranga',
    natureza: 'PF',
    modalidade: 'CONVENCIONAL',
    status: 'RASCUNHO_DRE',
    km: '9.80',
    naoCadeirante: 20,
    diaFaltaVeiculo: 6,
  },
  {
    ordemServicoId: 'os-8',
    numeroOs: 1008,
    credenciadoId: 'c-8',
    credenciadoNome: 'Ana Souza Transporte Escolar',
    dreId: 'dre-4',
    dreCodigo: 'IP',
    dreNome: 'Ipiranga',
    natureza: 'PF',
    modalidade: 'ACESSIVEL',
    status: 'ENVIADO_SME',
    km: '15.60',
    naoCadeirante: 8,
    cadeirante: 4,
    diaFalta: 3,
  },
];

/** Monta a grade diária de um mês a partir dos perfis fixos acima. */
function construirApontamentosDoMes(ano: number, mes: number): ApontamentoDiaLinha[] {
  const diasLetivos = diasUteisDoMes(ano, mes);
  const mesReferencia = `${ano}-${String(mes).padStart(2, '0')}-01`;
  return PERFIS_OS_APONTAMENTO.map((perfil) => {
    const diasVazios = perfil.todosVazios ? diasLetivos : (perfil.diasVazios ?? []);
    return {
      ordemServicoId: perfil.ordemServicoId,
      numeroOs: perfil.numeroOs,
      credenciadoId: perfil.credenciadoId,
      credenciadoNome: perfil.credenciadoNome,
      condutorNome: perfil.condutorNome ?? null,
      condutorCrmc: perfil.condutorCrmc ?? null,
      placaVeiculo: perfil.placaVeiculo ?? null,
      tipoVeiculo: perfil.tipoVeiculo ?? null,
      monitorNome: perfil.monitorNome ?? null,
      dreId: perfil.dreId,
      dreCodigo: perfil.dreCodigo,
      dreNome: perfil.dreNome,
      natureza: perfil.natureza,
      modalidade: perfil.modalidade,
      status: perfil.status,
      mesReferencia,
      km: perfil.km,
      vigenciaInicio: null,
      vigenciaFim: perfil.vigenciaFimDia
        ? `${ano}-${String(mes).padStart(2, '0')}-${String(perfil.vigenciaFimDia).padStart(2, '0')}`
        : null,
      diasLetivos,
      celulas: gerarCelulas(diasLetivos, perfil, {
        diasVazios,
        diaFalta: perfil.diaFalta,
        diaFaltaVeiculo: perfil.diaFaltaVeiculo,
      }),
    };
  });
}

function apontamentosRelativos(deslocamento: number): ApontamentoDiaLinha[] {
  const [ano, mes] = competenciaRelativa(deslocamento).split('-').map(Number);
  return construirApontamentosDoMes(ano!, mes!);
}

/** O mês anterior é a competência-alvo padrão do Fechamento Mensal V2 — por
 * isso o mock cobre o mês anterior além do corrente, pra a grade de
 * apontamento não abrir vazia ao entrar direto no fechamento. */
export const MOCK_APONTAMENTO_DIAS: ApontamentoDiaLinha[] = [
  ...apontamentosRelativos(-1),
  ...apontamentosRelativos(0),
];

const MOCK_COMPOSICAO_VALORES_MES_CORRENTE: ComposicaoValoresLinha[] = [
  {
    credenciadoId: 'c-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    ordemServicoId: 'os-1c',
    numeroOs: 1101,
    dreId: 'dre-1',
    mesReferencia: competenciaRelativa(0),
    valorBase: '25150.00',
    descontoFaltas: '150.00',
    glosaVeiculo: '0.00',
    ajustesPositivos: '0.00',
    ajustesNegativos: '0.00',
    valorFinal: '25000.00',
  },
  {
    credenciadoId: 'c-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    ordemServicoId: 'os-1d',
    numeroOs: 1102,
    dreId: 'dre-1',
    mesReferencia: competenciaRelativa(0),
    valorBase: '18200.00',
    descontoFaltas: '0.00',
    glosaVeiculo: '600.00',
    ajustesPositivos: '0.00',
    ajustesNegativos: '0.00',
    valorFinal: '17600.00',
  },
  {
    credenciadoId: 'c-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    ordemServicoId: 'os-3c',
    numeroOs: 1103,
    dreId: 'dre-3',
    mesReferencia: competenciaRelativa(0),
    valorBase: '41800.00',
    descontoFaltas: '0.00',
    glosaVeiculo: '0.00',
    ajustesPositivos: '500.00',
    ajustesNegativos: '0.00',
    valorFinal: '42300.00',
  },
  {
    credenciadoId: 'c-4',
    credenciadoNome: 'João Motorista ME',
    ordemServicoId: 'os-4c',
    numeroOs: 1104,
    dreId: 'dre-1',
    mesReferencia: competenciaRelativa(0),
    valorBase: '22400.00',
    descontoFaltas: '400.00',
    glosaVeiculo: '0.00',
    ajustesPositivos: '0.00',
    ajustesNegativos: '200.00',
    valorFinal: '21800.00',
  },
];

/** Mesma razão da grade de apontamento: o mês anterior é a competência-alvo padrão. */
const MOCK_COMPOSICAO_VALORES_MES_ANTERIOR: ComposicaoValoresLinha[] =
  MOCK_COMPOSICAO_VALORES_MES_CORRENTE.map((c) => ({
    ...c,
    mesReferencia: competenciaRelativa(-1),
  }));

export const MOCK_COMPOSICAO_VALORES: ComposicaoValoresLinha[] = [
  ...MOCK_COMPOSICAO_VALORES_MES_ANTERIOR,
  ...MOCK_COMPOSICAO_VALORES_MES_CORRENTE,
];

export const MOCK_LOTES_ATESTE: LoteAtesteImportado[] = [
  {
    id: 'lote-at-1',
    arquivoNome: 'pmsp-agosto-pf.xlsx',
    numeroOs: 1001,
    crmc: 'CRMC-4521',
    placa: 'ABC1D23',
    temInconsistencia: false,
    quantidadeInconsistencias: 0,
    empresa: 'Transportes Silva & Cia Ltda',
    natureza: 'PF',
    dreId: 'dre-1',
    contrato: 'CT-2026-014',
    diasValidos: 22,
    diasTotal: 22,
    statusConciliacao: 'AUTOMATICA',
  },
];

/* =====================================================================
 * SOF — abas restantes (FIN-031.x)
 * ===================================================================== */

export const MOCK_RESERVAS_SOF: ReservaSofLinha[] = [
  {
    id: 'resv-1',
    codigoReserva: 'RESV-2026-0812',
    dotacao: '16.10.12.365.3013.2100',
    saldoReserva: '4250000.00',
    subfuncaoCodigo: '365',
    subfuncaoTexto: 'Educação infantil',
    programa: '3013',
    projetoAtividade: '2100',
    contaDespesa: '3.3.90.39',
    competencia: COMPETENCIA_RECENTE,
    ultimaSincronizacao: '2026-08-25T07:00:00Z',
  },
];

export const MOCK_STATUS_SOF: StatusSofLinha[] = [
  {
    id: 'm-1',
    medicaoId: 'm-1',
    mesReferencia: COMPETENCIA_RECENTE,
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    valor: '22800.00',
    estagioSof: 'CONFIRMADO_SOF',
    pagavel: true,
  },
  {
    id: 'm-3',
    medicaoId: 'm-3',
    mesReferencia: COMPETENCIA_RECENTE,
    credenciadoNome: 'Cooperativa de Vans SP',
    valor: '38070.00',
    estagioSof: 'PROCESSADA',
    pagavel: false,
  },
];

export const MOCK_CONCILIACAO_SOF: ConciliacaoSofItem[] = [
  {
    medicaoId: 'm-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    estagioSof: 'PAGA',
    valorMedicao: '22800.00',
    valorPago: '22800.00',
    classificacao: 'CONCILIADO',
  },
  {
    medicaoId: 'm-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    estagioSof: 'PAGA',
    valorMedicao: '38070.00',
    valorPago: '37900.00',
    classificacao: 'DIVERGENTE',
  },
];

export const MOCK_RELATORIO_FISCAL_CONSOLIDADO: RelatorioFiscalConsolidadoDre[] = [
  {
    id: 'rel-cons-1',
    dreId: 'dre-1',
    dreNome: 'Butantã',
    totalCredenciados: 45,
    valorBruto: '1500000.00',
    valorLiquido: '1495000.00',
    valorDistribuido: '1495000.00',
    valorPago: '1495000.00',
    status: 'CONCLUIDO',
  },
];

export const MOCK_RELATORIO_FISCAL_CREDENCIADO: RelatorioFiscalCredenciado[] = [
  {
    id: 'rel-cred-1',
    dreId: 'dre-1',
    dreNome: 'Butantã',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    demonstrativo: 'DEM-2026-08-0142',
    valorLiquido: '22800.00',
    status: 'PAGO',
  },
];

export const MOCK_HISTORICO_EXPORTACAO_FISCAL: ExportacaoFiscalHistoricoLinha[] = [
  {
    id: 'exp-1',
    numero: 'REL-2026-08-001',
    tipo: 'Consolidado DRE',
    formato: 'PDF',
    competencia: COMPETENCIA_RECENTE,
    registros: 4,
    valor: '4250000.00',
    protocoloSei: 'SEI-6017.2026/0009911-2',
    hashSha256: 'f3a1c9e7b2d84610a5e93c7f0b1d4a8e6c2f9017b3d5e8a4c6f0912b7d3e5a9c',
    geradoEm: '2026-08-25T16:00:00Z',
  },
];

/* =====================================================================
 * Apuração — acompanhamento, medições e lançamento
 * ===================================================================== */

export const MOCK_ACOMPANHAMENTO_MENSAL: AcompanhamentoMensalLinha[] = [
  {
    credenciadoId: 'c-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    cpfCnpj: '12.345.678/0001-90',
    valorTotal: '25000.00',
    etapaApontamento: true,
    etapaSme: 'OK',
    etapaCalculada: true,
    etapaEmpenho: true,
    docsPendentes: 0,
    etapaLiquidada: false,
    etapaSof: true,
    etapaPaga: false,
  },
  {
    credenciadoId: 'c-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    cpfCnpj: '23.456.789/0001-01',
    valorTotal: '42300.00',
    etapaApontamento: true,
    etapaSme: 'OK',
    etapaCalculada: true,
    etapaEmpenho: false,
    docsPendentes: 2,
    etapaLiquidada: false,
    etapaSof: false,
    etapaPaga: false,
  },
  {
    credenciadoId: 'c-4',
    credenciadoNome: 'João Motorista ME',
    cpfCnpj: '345.678.901-02',
    valorTotal: '21800.00',
    etapaApontamento: true,
    etapaSme: 'DEVOLVIDO',
    etapaCalculada: false,
    etapaEmpenho: false,
    docsPendentes: 0,
    etapaLiquidada: false,
    etapaSof: false,
    etapaPaga: false,
  },
  {
    credenciadoId: 'c-2',
    credenciadoNome: 'Viação Escolar Zona Leste',
    cpfCnpj: '34.567.890/0001-12',
    valorTotal: '18500.00',
    etapaApontamento: false,
    etapaSme: 'PENDENTE',
    etapaCalculada: false,
    etapaEmpenho: false,
    docsPendentes: 0,
    etapaLiquidada: false,
    etapaSof: false,
    etapaPaga: false,
  },
];

export const MOCK_LANCAMENTO_OS: Record<string, LancamentoOsFormulario> = {
  'os-1': {
    ordemServicoId: 'os-1',
    numeroOs: 1001,
    tipoTeg: 'Van escolar',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    kmRotaBase: '45.50',
    diasUteis: 22,
    diasOperados: 22,
    kmRodados: '1001.00',
    ativPresencial: 0,
    ativRemoto: 0,
    ativExterna: 0,
    atendimentosCadeirante: 2,
    atendimentosNaoCadeirante: 18,
    ajusteValor: '0.00',
    ajusteDescricao: '',
    observacoes: '',
  },
  'os-2': {
    ordemServicoId: 'os-2',
    numeroOs: 1002,
    tipoTeg: 'Van escolar',
    credenciadoNome: 'Viação Escolar Zona Leste',
    kmRotaBase: '30.00',
    diasUteis: 22,
    diasOperados: 20,
    kmRodados: '600.00',
    ativPresencial: 1,
    ativRemoto: 0,
    ativExterna: 0,
    atendimentosCadeirante: 0,
    atendimentosNaoCadeirante: 25,
    ajusteValor: '-150.00',
    ajusteDescricao: 'Rota reduzida por obra',
    observacoes: '',
  },
  'os-3': {
    ordemServicoId: 'os-3',
    numeroOs: 1003,
    tipoTeg: 'Micro-ônibus',
    credenciadoNome: 'Cooperativa de Vans SP',
    kmRotaBase: '12.40',
    diasUteis: 22,
    diasOperados: 22,
    kmRodados: '272.80',
    ativPresencial: 0,
    ativRemoto: 0,
    ativExterna: 0,
    atendimentosCadeirante: 0,
    atendimentosNaoCadeirante: 30,
    ajusteValor: '500.00',
    ajusteDescricao: '',
    observacoes: '',
  },
};

/* =====================================================================
 * Demonstrativos + Aprovação SME (financeiro) + Apontamentos standalone
 * ===================================================================== */

export const MOCK_DEMONSTRATIVOS: DemonstrativoLinha[] = [
  {
    credenciadoId: 'c-1',
    credenciadoNome: 'Transportes Silva & Cia Ltda',
    cpfCnpj: '12.345.678/0001-90',
    tipoPessoa: 'PJ',
    mesReferencia: competenciaRelativa(-1),
    ajustesQuantidade: 1,
    ajustesSoma: '-150.00',
    statusCredenciado: 'ENVIADO',
    concordanciaEm: null,
    motivoRecusa: null,
    valorTotal: '22800.00',
    devolvida: false,
  },
  {
    credenciadoId: 'c-3',
    credenciadoNome: 'Cooperativa de Vans SP',
    cpfCnpj: '23.456.789/0001-11',
    tipoPessoa: 'PJ',
    mesReferencia: competenciaRelativa(-1),
    ajustesQuantidade: 0,
    ajustesSoma: '0.00',
    statusCredenciado: 'GERADO',
    concordanciaEm: null,
    motivoRecusa: null,
    valorTotal: '38070.00',
    devolvida: false,
  },
  {
    credenciadoId: 'c-4',
    credenciadoNome: 'João Motorista ME',
    cpfCnpj: '345.678.901-02',
    tipoPessoa: 'PF',
    mesReferencia: competenciaRelativa(-1),
    ajustesQuantidade: 0,
    ajustesSoma: '0.00',
    statusCredenciado: 'VISUALIZADO',
    concordanciaEm: null,
    motivoRecusa: null,
    valorTotal: '21800.00',
    devolvida: false,
  },
  {
    credenciadoId: 'c-2',
    credenciadoNome: 'Viação Escolar Zona Leste',
    cpfCnpj: '34.567.890/0001-12',
    tipoPessoa: 'PJ',
    mesReferencia: competenciaRelativa(-1),
    ajustesQuantidade: 2,
    ajustesSoma: '300.00',
    statusCredenciado: 'ENVIADO',
    concordanciaEm: '2026-08-12T09:30:00Z',
    motivoRecusa: null,
    valorTotal: '18500.00',
    devolvida: false,
  },
  {
    credenciadoId: 'c-6',
    credenciadoNome: 'Vans Unidas Cooperativa',
    cpfCnpj: '45.678.901/0001-13',
    tipoPessoa: 'PJ',
    mesReferencia: competenciaRelativa(-1),
    ajustesQuantidade: 1,
    ajustesSoma: '-500.00',
    statusCredenciado: 'RECUSADO',
    concordanciaEm: null,
    motivoRecusa: 'Divergência no KM apontado da terceira semana.',
    valorTotal: '15200.00',
    devolvida: true,
  },
];

export const MOCK_FECHAMENTO_V2_LISTA: FechamentoV2ListaItem[] = [
  {
    id: `${competenciaRelativa(-1)}__PF`,
    mesReferencia: competenciaRelativa(-1),
    natureza: 'PF',
    etapa: 'processamento',
    responsavel: 'DTP',
    proximoPasso: 'Concluir processamento das medições aprovadas',
    atrasado: true,
    diasParaPrazo: -2,
    totalOs: 6,
    totalOsPagas: 0,
    valorPago: '0.00',
    valorCalculado: '60000.00',
    divergencia: false,
  },
  {
    id: `${competenciaRelativa(-1)}__PJ`,
    mesReferencia: competenciaRelativa(-1),
    natureza: 'PJ',
    etapa: 'validacao',
    responsavel: 'SME',
    proximoPasso: 'Aguardando validação dos lotes SME',
    atrasado: false,
    diasParaPrazo: 3,
    totalOs: 4,
    totalOsPagas: 0,
    valorPago: '0.00',
    valorCalculado: '38070.00',
    divergencia: false,
  },
  {
    id: `${competenciaRelativa(-2)}__PF`,
    mesReferencia: competenciaRelativa(-2),
    natureza: 'PF',
    etapa: 'concluido',
    responsavel: '—',
    proximoPasso: 'Competência encerrada',
    atrasado: false,
    diasParaPrazo: 0,
    totalOs: 5,
    totalOsPagas: 5,
    valorPago: '900000.00',
    valorCalculado: '900000.00',
    divergencia: false,
  },
  {
    id: `${competenciaRelativa(-2)}__PJ`,
    mesReferencia: competenciaRelativa(-2),
    natureza: 'PJ',
    etapa: 'pagamento',
    responsavel: 'DTP',
    proximoPasso: 'Confirmar pagamento no SOF',
    atrasado: false,
    diasParaPrazo: 5,
    totalOs: 3,
    totalOsPagas: 2,
    valorPago: '22800.00',
    valorCalculado: '38070.00',
    divergencia: true,
  },
  {
    id: `${competenciaRelativa(-3)}__PF`,
    mesReferencia: competenciaRelativa(-3),
    natureza: 'PF',
    etapa: 'concluido',
    responsavel: '—',
    proximoPasso: 'Competência encerrada',
    atrasado: false,
    diasParaPrazo: 0,
    totalOs: 5,
    totalOsPagas: 5,
    valorPago: '870000.00',
    valorCalculado: '870000.00',
    divergencia: false,
  },
  {
    id: `${competenciaRelativa(-3)}__PJ`,
    mesReferencia: competenciaRelativa(-3),
    natureza: 'PJ',
    etapa: 'concluido',
    responsavel: '—',
    proximoPasso: 'Competência encerrada',
    atrasado: false,
    diasParaPrazo: 0,
    totalOs: 3,
    totalOsPagas: 3,
    valorPago: '38070.00',
    valorCalculado: '38070.00',
    divergencia: false,
  },
  {
    id: `${competenciaRelativa(-4)}__PF`,
    mesReferencia: competenciaRelativa(-4),
    natureza: 'PF',
    etapa: 'concluido',
    responsavel: '—',
    proximoPasso: 'Competência encerrada',
    atrasado: false,
    diasParaPrazo: 0,
    totalOs: 5,
    totalOsPagas: 5,
    valorPago: '855000.00',
    valorCalculado: '855000.00',
    divergencia: false,
  },
  {
    id: `${competenciaRelativa(-5)}__PF`,
    mesReferencia: competenciaRelativa(-5),
    natureza: 'PF',
    etapa: 'concluido',
    responsavel: '—',
    proximoPasso: 'Competência encerrada',
    atrasado: false,
    diasParaPrazo: 0,
    totalOs: 4,
    totalOsPagas: 4,
    valorPago: '780000.00',
    valorCalculado: '780000.00',
    divergencia: false,
  },
];

/** Pipeline do ciclo orçamentário-financeiro (Empenho → Liquidação →
 * Pagamento) — mesmos 7 estágios do `PipelineFinanceiro` já usado no Painel
 * Executivo. Valores fixos por enquanto: o motor de cálculo que os produziria
 * de verdade ainda não existe. */
export function construirCicloFinanceiroPipeline(): {
  docsACadastrar: number;
  docsCadastrados: number;
  etapas: {
    chave: string;
    rotulo: string;
    tom: 'pendente' | 'ativo' | 'concluido';
    qtd: number;
    valor: number;
  }[];
} {
  return {
    docsACadastrar: 1,
    docsCadastrados: 12,
    etapas: [
      { chave: 'a_empenhar', rotulo: 'A empenhar', tom: 'pendente', qtd: 4, valor: 18000 },
      { chave: 'empenhado', rotulo: 'Empenhado', tom: 'ativo', qtd: 3, valor: 60000 },
      { chave: 'a_atestar', rotulo: 'A atestar', tom: 'pendente', qtd: 2, valor: 12000 },
      { chave: 'atestado', rotulo: 'Atestado', tom: 'ativo', qtd: 2, valor: 42300 },
      { chave: 'a_liquidar', rotulo: 'A liquidar', tom: 'pendente', qtd: 1, valor: 8000 },
      { chave: 'liquidado', rotulo: 'Liquidado', tom: 'ativo', qtd: 1, valor: 25000 },
      { chave: 'autorizado', rotulo: 'Autorizado', tom: 'concluido', qtd: 1, valor: 21800 },
    ],
  };
}

/** Chave `AAAA-MM__NATUREZA` do resumo, relativa ao mês corrente. */
function chaveResumo(deslocamento: number, natureza: 'PF' | 'PJ'): string {
  return `${competenciaRelativa(deslocamento).slice(0, 7)}__${natureza}`;
}

export const MOCK_FECHAMENTO_V2_RESUMO: Record<string, FechamentoV2Resumo> = {
  [chaveResumo(-1, 'PJ')]: {
    etapa: 'validacao',
    responsavel: 'SME',
    proximoPasso: 'Aguardando validação dos lotes SME',
    atrasado: false,
    diasParaPrazo: 3,
    totalOs: 4,
    totalOsPagas: 0,
    valorPago: '0.00',
    valorCalculado: '38070.00',
    contagemAprovada: 0,
    contagemRascunho: 1,
    contagemProcessada: 3,
    bloqueiosTotal: 0,
    medicoesValidaveis: 0,
    cicloEmpenho: 0,
    cicloLiquidacao: 0,
    cicloAutorizacao: 0,
  },
  [chaveResumo(-1, 'PF')]: {
    etapa: 'processamento',
    responsavel: 'DTP',
    proximoPasso: 'Concluir processamento das medições aprovadas',
    atrasado: true,
    diasParaPrazo: -2,
    totalOs: 6,
    totalOsPagas: 0,
    valorPago: '0.00',
    valorCalculado: '60000.00',
    contagemAprovada: 1,
    contagemRascunho: 2,
    contagemProcessada: 1,
    bloqueiosTotal: 3,
    medicoesValidaveis: 1,
    cicloEmpenho: 51,
    cicloLiquidacao: 28,
    cicloAutorizacao: 23,
  },
};

export const MOCK_LOTES_SME_MESTRE: LoteSmeMestre[] = [
  {
    id: 'lote-sme-1',
    dreId: 'dre-1',
    dreNome: 'Butantã',
    tipoPessoa: 'PJ',
    credenciados: [
      {
        credenciadoId: 'c-1',
        credenciadoNome: 'Transportes Silva & Cia Ltda',
        cpfCnpj: '12.345.678/0001-90',
        dataEnvio: '2026-08-18T09:00:00Z',
        diasDigitados: 22,
        alunos: 45,
        status: 'AGUARDANDO',
      },
      {
        credenciadoId: 'c-2',
        credenciadoNome: 'Viação Oeste Escolar Ltda',
        cpfCnpj: '23.456.789/0001-11',
        dataEnvio: '2026-08-18T09:10:00Z',
        diasDigitados: 21,
        alunos: 38,
        status: 'AGUARDANDO',
      },
    ],
  },
  {
    id: 'lote-sme-2',
    dreId: 'dre-2',
    dreNome: 'Campo Limpo',
    tipoPessoa: 'PF',
    credenciados: [
      {
        credenciadoId: 'c-3',
        credenciadoNome: 'Marcos Antônio de Souza',
        cpfCnpj: '345.678.901-22',
        dataEnvio: '2026-08-17T14:00:00Z',
        diasDigitados: 20,
        alunos: 12,
        status: 'AGUARDANDO',
      },
    ],
  },
  {
    id: 'lote-sme-3',
    dreId: 'dre-4',
    dreNome: 'Ipiranga',
    tipoPessoa: 'PJ',
    credenciados: [
      {
        credenciadoId: 'c-4',
        credenciadoNome: 'Cooperativa Escolar Ipiranga',
        cpfCnpj: '34.567.890/0001-45',
        dataEnvio: '2026-08-10T11:00:00Z',
        diasDigitados: 22,
        alunos: 60,
        status: 'APROVADO',
      },
    ],
  },
  {
    id: 'lote-sme-4',
    dreId: 'dre-5',
    dreNome: 'Capela do Socorro',
    tipoPessoa: 'PF',
    credenciados: [
      {
        credenciadoId: 'c-5',
        credenciadoNome: 'Joana D’Arc Ferreira',
        cpfCnpj: '456.789.012-33',
        dataEnvio: '2026-08-09T08:30:00Z',
        diasDigitados: 19,
        alunos: 10,
        status: 'DEVOLVIDO',
      },
    ],
  },
];
